import { useCallback, useEffect, useMemo, useState } from "react";
import { ExternalLink, Minimize2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import BntAsistentePanelContent, { BNT_ASISTENTE_TABS } from "@/components/asistente/BntAsistentePanelContent";
import BntAsistenteClearHistoryButton from "@/components/asistente/BntAsistenteClearHistoryButton";
import BntAsistenteUnreadTasksModal from "@/components/asistente/BntAsistenteUnreadTasksModal";
import { useAuth } from "@/lib/auth";
import { clearAssistantPopupRef, registerAssistantPopup } from "@/modules/asistente/bntAsistenteRoutes";
import {
  BNT_MSG_MINIMIZE,
  focusOrOpenMainSystem,
  postToOpener,
} from "@/modules/asistente/bntAsistenteMessaging";
import { useBntAsistenteSummary } from "@/modules/asistente/useBntAsistenteSummary";

/**
 * Ventana compacta BNT Asistente — popup / ruta /asistente.
 */
export default function BntAsistenteCompact() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") || "interno";
  const [tab, setTab] = useState(
    BNT_ASISTENTE_TABS.some((t) => t.id === tabParam) ? tabParam : "interno",
  );
  const isCompact = searchParams.get("compact") === "1";
  const [designerChatTarget, setDesignerChatTarget] = useState(null);
  const { summary, loading, reload } = useBntAsistenteSummary({
    enabled: Boolean(user),
    keepaliveSession: isCompact,
  });

  const setMainTab = useCallback((id) => {
    setTab(id);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", id);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  useEffect(() => {
    if (!isCompact) return undefined;
    registerAssistantPopup(window);
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (data?.type === "bnt-asistente:set-tab" && data.tab) {
        setMainTab(data.tab);
      }
    };
    window.addEventListener("message", onMessage);
    const onUnload = () => clearAssistantPopupRef();
    window.addEventListener("beforeunload", onUnload);
    return () => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener("beforeunload", onUnload);
    };
  }, [isCompact, setMainTab]);

  const headerCounts = useMemo(
    () => ({
      alerts: summary.alertsCount,
      chat: summary.teamChatHintCount + summary.clientMessagesCount,
    }),
    [summary],
  );

  /** Abre/enfoca el sistema completo; la ventana flotante sigue abierta para monitoreo. */
  const handleOpenFull = () => {
    focusOrOpenMainSystem({ path: "/" });
  };

  /** Minimiza al dock integrado de la pestaña principal y cierra el popup. */
  const handleMinimize = () => {
    if (isCompact) {
      const sent = postToOpener({ type: BNT_MSG_MINIMIZE, tab });
      if (sent && window.opener && !window.opener.closed) {
        window.opener.focus();
        window.close();
        return;
      }
    }
    window.blur();
  };

  /** Módulo del sistema → pantalla completa; asistente a la barra de tareas. */
  const handleOpenInMainSystem = useCallback((path) => {
    focusOrOpenMainSystem({ path: path || "/" });
    if (isCompact) {
      const sent = postToOpener({ type: BNT_MSG_MINIMIZE, tab });
      if (sent && window.opener && !window.opener.closed) {
        window.opener.focus();
        window.close();
        return;
      }
    }
    if (isCompact) {
      window.blur();
    }
  }, [isCompact, tab]);

  const handlePersonalNotesChanged = useCallback(() => {
    reload?.();
  }, [reload]);

  const [contentKey, setContentKey] = useState(0);
  const handleHistoryCleared = useCallback(() => {
    reload?.();
    setContentKey((k) => k + 1);
  }, [reload]);

  const handleUnreadAttention = useCallback(({ tab: nextTab } = {}) => {
    if (nextTab) setMainTab(nextTab);
    focusOrOpenMainSystem({ path: "/" });
  }, [setMainTab]);

  return (
    <div
      className="h-dvh max-h-dvh flex flex-col overflow-hidden bg-[#F4F6F8] text-slate-900"
      data-testid="bnt-asistente-compact"
    >
      <BntAsistenteUnreadTasksModal onAttention={handleUnreadAttention} />
      <header className="shrink-0 border-b border-slate-200 bg-white px-3 py-2 space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-sm font-bold text-[#0F2D5C] m-0 truncate">BNT Asistente</h1>
            <p className="text-[10px] text-slate-500 m-0 truncate">
              {user?.email || user?.name || "Sesión activa"}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0 text-[10px] text-slate-600">
            {headerCounts.alerts ? <span>{headerCounts.alerts} alertas</span> : null}
            {headerCounts.chat ? <span>{headerCounts.chat} mensajes</span> : null}
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          <BntAsistenteClearHistoryButton
            onCleared={handleHistoryCleared}
            target={designerChatTarget}
            requireTarget
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-sm h-7 text-[11px] flex-1 min-w-[120px]"
            onClick={handleOpenFull}
            data-testid="bnt-asistente-open-full"
          >
            <ExternalLink className="h-3 w-3 mr-1" aria-hidden />
            Abrir sistema completo
          </Button>
          {isCompact ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-sm h-7 px-2"
              onClick={handleMinimize}
              title="Minimizar a barra de tareas del sistema principal"
              data-testid="bnt-asistente-minimize"
            >
              <Minimize2 className="h-3.5 w-3.5" aria-hidden />
            </Button>
          ) : null}
        </div>
      </header>

      <main className="flex-1 min-h-0 overflow-hidden">
        <BntAsistentePanelContent
          key={contentKey}
          user={user}
          summary={summary}
          loading={loading}
          tab={tab}
          onTabChange={setMainTab}
          showHeaderCounts={false}
          onOpenInMainSystem={handleOpenInMainSystem}
          onOpenAlert={handleOpenInMainSystem}
          onPersonalNotesChanged={handlePersonalNotesChanged}
          onAssignedTasksChanged={handlePersonalNotesChanged}
          designerChatTarget={designerChatTarget}
          onDesignerChatTargetChange={setDesignerChatTarget}
        />
      </main>

      {tab !== "interno" ? (
        <footer className="shrink-0 border-t border-slate-200 bg-white px-3 py-1.5 text-[10px] text-slate-400 text-center leading-relaxed">
          Ventana independiente · puede anclar esta URL en la barra de tareas · «Abrir sistema completo» no cierra el asistente
        </footer>
      ) : null}
    </div>
  );
}
